import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import useAuthenticatedAxios from '../../auth/AuthenticatedAxios';
import { useAuth0 } from '@auth0/auth0-react';
import UserList from '../UserList';
import TabContainer from '../templates/TabContainer';
import { onVideoCallStarted } from '../../socket';

// Styled components - Synthora-inspired design
const ContentContainer = styled.div`
  background: linear-gradient(135deg, #f9fbff 0%, #ffffff 50%, #f9fbff 100%);
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  min-height: 0;
  
  @media (max-width: 768px) {
    height: 100%;
    min-height: 0;
  }
`;

const MainLayout = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  height: 100%;
  flex: 1;
  background: transparent;
  gap: 0;
  overflow: hidden;
  min-height: 0;
  
  @media (max-width: 768px) {
    flex-direction: column;
    height: 100%;
    min-height: 0;
    overflow: hidden;
    position: relative;
  }
`;

const SidebarContainer = styled.div`
  width: 320px;
  min-width: 320px;
  flex-shrink: 0;
  height: 100%;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(255, 255, 255, 0.98) 100%);
  border-right: 1px solid #e0e8f5;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  
  @media (max-width: 768px) {
    position: fixed;
    bottom: 50px;
    left: 0;
    width: 100%;
    min-width: 100%;
    height: calc(100vh - 100px);
    max-height: calc(100vh - 100px);
    z-index: 200;
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(255, 255, 255, 0.98) 100%);
    transition: transform 0.3s ease-in-out;
    box-shadow: ${props => props.$isMobileVisible ? '0 -4px 20px rgba(39, 68, 131, 0.14)' : 'none'};
    overflow-y: hidden;
    transform: translateY(${props => props.$isMobileVisible ? '0' : '100%'});
    visibility: ${props => props.$isMobileVisible ? 'visible' : 'hidden'};
    pointer-events: ${props => props.$isMobileVisible ? 'auto' : 'none'};
    border-radius: 0;
    border: none;
    border-top: 1px solid rgba(91, 97, 234, 0.15);
  }
`;

const ChatContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  background: transparent;
  overflow: hidden;
  min-height: 0;
  
  @media (max-width: 768px) {
    width: 100%;
    flex: 1;
    height: 100%;
    min-height: 0;
    overflow: hidden;
    position: relative;
    z-index: 1;
  }
`;

const Overlay = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: ${props => props.$isMobileVisible ? 'block' : 'none'};
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100vh;
    background-color: rgba(23, 36, 62, 0.35);
    z-index: 199;
  }
`;

const CloseBar = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: ${props => props.$isMobileVisible ? 'flex' : 'none'};
    width: 50px;
    height: 4px;
    background-color: #dce5f4;
    border-radius: 4px;
    margin: 10px auto;
  }
`;

const ConversationPage = () => {
  const authenticatedAxios = useAuthenticatedAxios();
  const { user, isAuthenticated, isLoading } = useAuth0();
  const { threadId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [expertId, setExpertId] = useState(null);
  const [conversation, setConversation] = useState(null);
  const [emojiPickerVisible, setEmojiPickerVisible] = useState(false);
  const [message, setMessage] = useState('');
  const [conversationList, setConversationList] = useState([]);
  const [isMobileVisible, setIsMobileVisible] = useState(false);
  const [videoCallParams, setVideoCallParams] = useState(null);
  const [initialActiveTab, setInitialActiveTab] = useState(0);

  // Check if the toggle parameter is in the URL
  useEffect(() => {
    if (location.search.includes('toggle=true')) {
      setIsMobileVisible(true);
    }
  }, [location]);

  // Clean up welcome param from URL if present (user redirected from widget with meetings disabled)
  useEffect(() => {
    if (location.search.includes('welcome=true')) {
      // Remove the welcome param from URL without triggering a reload
      const newUrl = location.pathname;
      navigate(newUrl, { replace: true });
    }
  }, [location, navigate]);

  useEffect(() => {
    const handleVideoCallStarted = (data) => {
      console.log('Video call started:', data);
      // setVideoCallParams(data);
      // setInitialActiveTab(1);
    };
    const unsubscribe = onVideoCallStarted(handleVideoCallStarted);
    return () => {
      unsubscribe && unsubscribe();
    };
  }, []);

  useEffect(() => {
    const fetchExpertList = async () => {
      try {
        const conversationsList = await authenticatedAxios.get(`/api/experts/conversations`);
        setConversationList(conversationsList.data);

        if (threadId != null && threadId.trim() != '') {
          const convo = conversationsList.data.filter(x => x.threadId == threadId);
          if (convo && convo.length > 0) {
            setExpertId(convo[0].expertId);
            // Hide sidebar when a conversation is selected on mobile
            if (window.innerWidth <= 768) {
              setIsMobileVisible(false);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching conversations list:', error);
      }
    };
    fetchExpertList();
  }, []);

  const selectConversation = (newConversation, expertId) => {
    setExpertId(expertId);

    // Ensure all conversation data is preserved
    setConversation(newConversation);

    // Hide sidebar when a conversation is selected on mobile
    if (window.innerWidth <= 768) {
      setIsMobileVisible(false);
    }

  };


  const updateConversationViaSocket = (updatedConversation) => {
    setConversation(updatedConversation);
  };

  const toggleMobileView = () => {
    setIsMobileVisible(!isMobileVisible);
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768 && location.search.includes('toggle=true')) {
        navigate('/conversations', { replace: true });
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [location, navigate]);

  return (
    <ContentContainer>
      <Overlay $isMobileVisible={isMobileVisible} onClick={toggleMobileView} />

      <MainLayout>
        <SidebarContainer $isMobileVisible={isMobileVisible}>
          <CloseBar $isMobileVisible={isMobileVisible} onClick={toggleMobileView} />
          <UserList
            conversationsInit={conversationList}
            clientId={''}
            onConversationSelect={selectConversation}
            paramThreadId={threadId}
          />
        </SidebarContainer>

        <ChatContainer>
          <TabContainer
            conversation={conversation}
            expertId={expertId}
            setConversation={updateConversationViaSocket}
            videoCallParams={videoCallParams}
            initialActiveTab={initialActiveTab}
          />
        </ChatContainer>
      </MainLayout>
    </ContentContainer>
  );
};

export default ConversationPage;
